import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, euro, downloadFile } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DataPagination from "@/components/DataPagination";
import { HeadCell } from "@/components/SortLabel";
import DataTable from "@/components/DataTable";
import { useTableSort } from "@/lib/useTableSort";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, ListChecks, Upload, Search, FileUp, CheckCircle2, Loader2, BookText, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const emptyVoce = { codice: "", descrizione: "", unita_misura: "cad", prezzo_unitario: 0, categoria: "" };
const PAGE_SIZE = 10;
const MAP_FIELDS = [
  { key: "codice", label: "Codice" },
  { key: "descrizione", label: "Descrizione" },
  { key: "unita_misura", label: "Unità di misura" },
  { key: "prezzo_unitario", label: "Prezzo unitario" },
  { key: "categoria", label: "Categoria" },
];

export default function Listino() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [listini, setListini] = useState([]);
  const [tipoTab, setTipoTab] = useState("attivo");
  const [voci, setVoci] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const fileRef = useRef();

  // dialogs
  const [listinoDialog, setListinoDialog] = useState(false);
  const [listinoForm, setListinoForm] = useState({ nome: "", tipo: "attivo", descrizione: "" });
  const [editingListino, setEditingListino] = useState(null);
  const [voceOpen, setVoceOpen] = useState(false);
  const [voceForm, setVoceForm] = useState(emptyVoce);
  const [editingVoce, setEditingVoce] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);

  const loadListini = useCallback(() => api.get("/listini").then((r) => setListini(r.data)), []);
  useEffect(() => { loadListini(); }, [loadListini]);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("listino");
  const selected = listini.find((l) => l.id === selectedId) || null;
  const selectListino = useCallback((l) => setSearchParams(l ? { listino: l.id } : {}), [setSearchParams]);

  // Allinea il tab (attivo/subappalto) al listino selezionato via URL.
  useEffect(() => {
    if (selectedId && listini.length) {
      const l = listini.find((x) => x.id === selectedId);
      if (l) setTipoTab(l.tipo);
    }
  }, [selectedId, listini]);

  const loadVoci = useCallback((lid) => {
    if (!lid) return setVoci([]);
    api.get("/listino", { params: { listino_id: lid } }).then((r) => setVoci(r.data));
  }, []);

  useEffect(() => { loadVoci(selectedId); setPage(1); }, [selectedId, loadVoci]);

  const listiniTab = listini.filter((l) => l.tipo === tipoTab);

  const openNewListino = () => { setListinoForm({ nome: "", tipo: tipoTab, descrizione: "" }); setEditingListino(null); setListinoDialog(true); };
  const openEditListino = (l) => { setListinoForm({ nome: l.nome, tipo: l.tipo, descrizione: l.descrizione || "" }); setEditingListino(l.id); setListinoDialog(true); };

  const saveListino = async () => {
    if (!listinoForm.nome.trim()) return toast.error("Inserisci il nome del listino");
    try {
      if (editingListino) {
        await api.put(`/listini/${editingListino}`, listinoForm);
        setListinoDialog(false);
        await loadListini();
        toast.success("Listino aggiornato");
      } else {
        const { data } = await api.post("/listini", listinoForm);
        setListinoDialog(false);
        await loadListini();
        setTipoTab(data.tipo);
        selectListino(data);
        toast.success("Listino creato");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeListino = async (id) => {
    await api.delete(`/listini/${id}`);
    if (selected?.id === id) selectListino(null);
    loadListini();
    toast.success("Listino eliminato");
  };

  const openNewVoce = () => { setVoceForm(emptyVoce); setEditingVoce(null); setVoceOpen(true); };
  const openEditVoce = (v) => { setVoceForm(v); setEditingVoce(v.id); setVoceOpen(true); };

  const saveVoce = async () => {
    if (!voceForm.codice.trim() && !voceForm.descrizione.trim()) return toast.error("Inserisci codice o descrizione");
    try {
      const payload = { ...voceForm, prezzo_unitario: parseFloat(voceForm.prezzo_unitario) || 0, listino_id: selected.id };
      if (editingVoce) await api.put(`/listino/${editingVoce}`, payload);
      else await api.post("/listino", payload);
      toast.success(editingVoce ? "Voce aggiornata" : "Voce creata");
      setVoceOpen(false);
      loadVoci(selected.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeVoce = async (id) => { await api.delete(`/listino/${id}`); loadVoci(selected.id); };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/listino/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportData(res.data);
      setMapping(res.data.suggested_mapping || {});
      setImportOpen(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  const commitImport = async () => {
    if (!mapping.codice && !mapping.descrizione) return toast.error("Mappa almeno il Codice o la Descrizione");
    setImporting(true);
    try {
      const res = await api.post("/listino/import/commit", { rows: importData.rows, mapping, listino_id: selected.id });
      toast.success(`${res.data.inserted} voci importate`);
      setImportOpen(false);
      setImportData(null);
      loadVoci(selected.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const filtered = voci.filter((v) => v.codice.toLowerCase().includes(query.toLowerCase()) || v.descrizione.toLowerCase().includes(query.toLowerCase()));
  const voceColumns = [
    { id: "codice", label: "Codice", width: 130, sortable: true, sortValue: (v) => v.codice || "",
      cell: (v) => <span className="font-semibold text-blue-800">{v.codice}</span> },
    { id: "descrizione", label: "Descrizione", width: 340, sortable: true, sortValue: (v) => v.descrizione || "",
      cell: (v) => <span className="text-slate-800 font-sans">{v.descrizione}</span> },
    { id: "categoria", label: "Categoria", width: 130, sortable: true, sortValue: (v) => v.categoria || "",
      cell: (v) => <span className="text-slate-500">{v.categoria || "—"}</span> },
    { id: "um", label: "UM", width: 90, align: "center", sortable: true, sortValue: (v) => v.unita_misura || "",
      cell: (v) => <span className="text-slate-500">{v.unita_misura}</span> },
    { id: "prezzo", label: "Prezzo Unit.", width: 140, align: "right", sortable: true, sortValue: (v) => v.prezzo_unitario || 0,
      cell: (v) => <span className="font-semibold text-slate-900">{euro(v.prezzo_unitario)}</span> },
    { id: "azioni", label: "Azioni", width: 120, align: "right", sortable: false,
      cell: (v) => (
        <div className="flex items-center justify-end gap-0.5">
          <Button variant="ghost" size="icon" data-testid={`edit-voce-${v.id}`} onClick={() => openEditVoce(v)} className="h-8 w-8 text-slate-500 hover:text-blue-700"><Pencil className="h-4 w-4" /></Button>
          <ConfirmDelete title="Eliminare voce?" description="La voce verrà rimossa dal listino." actionTestid={`confirm-delete-voce-${v.id}`} onConfirm={() => removeVoce(v.id)}>
            <Button variant="ghost" size="icon" data-testid={`delete-voce-${v.id}`} className="h-8 w-8 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          </ConfirmDelete>
        </div>) },
  ];

  return (
    <>
      

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-5">
        <PageHeader title="Listini" subtitle="Più listini attivi e di subappalto, associabili alle commesse">
        <Button variant="outline" data-testid="template-listino-button"
          onClick={() => downloadFile("/listino/import/template", "template_listino.xlsx").catch(() => toast.error("Errore download template"))}>
          <Download className="h-4 w-4 mr-1.5" /> Template import
        </Button>
        <Button data-testid="add-listino-button" onClick={openNewListino}
          className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] transition-transform">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo listino
        </Button>
      </PageHeader>
        {!selected ? (
          <>
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              <button data-testid="tab-attivo" onClick={() => { setTipoTab("attivo"); selectListino(null); }}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tipoTab === "attivo" ? "bg-white text-blue-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                Listini attivi
              </button>
              <button data-testid="tab-subappalto" onClick={() => { setTipoTab("subappalto"); selectListino(null); }}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tipoTab === "subappalto" ? "bg-white text-amber-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                Listini subappalto
              </button>
            </div>

            {listiniTab.length === 0 ? (
              <Card className="rounded-md border-slate-200 shadow-sm">
                <div className="py-16 flex flex-col items-center text-center">
                  <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center mb-4"><BookText className="h-6 w-6 text-slate-400" /></div>
                  <p className="text-slate-600 font-medium">Nessun listino {tipoTab === "attivo" ? "attivo" : "di subappalto"}</p>
                  <p className="text-sm text-slate-400 mt-1 mb-4">Crea un listino per iniziare a inserire le voci.</p>
                  <Button variant="outline" onClick={openNewListino}><Plus className="h-4 w-4 mr-1.5" /> Nuovo listino</Button>
                </div>
              </Card>
            ) : (
              <div className="flex flex-col gap-2" data-testid="listini-list">
                {listiniTab.map((l) => (
                  <div key={l.id} data-testid={`listino-item-${l.id}`} onClick={() => selectListino(l)}
                    className="cursor-pointer flex items-start gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50/40 transition-colors">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-500">
                      <BookText className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-800">{l.nome}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{l.descrizione?.trim() ? l.descrizione : <span className="italic text-slate-300">Nessuna descrizione</span>}</div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" data-testid={`edit-listino-${l.id}`} onClick={() => openEditListino(l)} className="h-8 w-8 text-slate-400 hover:text-blue-700"><Pencil className="h-4 w-4" /></Button>
                      {isAdmin && (
                      <ConfirmDelete title="Eliminare listino?" description="Verranno eliminate tutte le voci del listino." actionTestid={`confirm-delete-listino-${l.id}`} onConfirm={() => removeListino(l.id)}>
                        <Button variant="ghost" size="icon" data-testid={`delete-listino-${l.id}`} className="h-8 w-8 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                      </ConfirmDelete>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Button variant="ghost" size="sm" data-testid="back-to-listini" onClick={() => selectListino(null)} className="text-slate-600 hover:text-blue-700 -ml-2 flex-shrink-0">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Tutti i listini
                </Button>
                <span className="text-slate-300">/</span>
                <h3 className="font-heading font-semibold text-slate-900 truncate">{selected.nome}</h3>
                <span className={`text-[11px] font-semibold uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${selected.tipo === "subappalto" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{selected.tipo}</span>
                <span className="text-xs text-slate-400 flex-shrink-0">· {filtered.length} voci</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-56">
                  <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="listino-search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Cerca voce…" className="pl-9 bg-white h-9" />
                </div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" data-testid="import-file-input" />
                <Button variant="outline" size="sm" data-testid="import-listino-button" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4 mr-1.5" /> Importa</Button>
                <Button size="sm" data-testid="add-voce-button" onClick={openNewVoce} className="bg-blue-700 hover:bg-blue-800"><Plus className="h-4 w-4 mr-1.5" /> Voce</Button>
              </div>
            </div>
            {filtered.length === 0 ? (
              <div className="py-14 text-center text-sm text-slate-400">{query ? "Nessun risultato." : "Listino vuoto. Aggiungi o importa voci."}</div>
            ) : (
              <DataTable tableKey="listino-voci" columns={voceColumns} rows={filtered} rowKey={(v) => v.id}
                rowTestId={(v) => `voce-row-${v.id}`} pageSize={PAGE_SIZE}
                containerClassName="max-h-[calc(100vh-320px)]" initialSort={{ key: "codice", dir: "asc" }} />
            )}
          </Card>
        )}
      </div>

      {/* Nuovo listino dialog */}
      <Dialog open={listinoDialog} onOpenChange={setListinoDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingListino ? "Modifica listino" : "Nuovo listino"}</DialogTitle>
            <DialogDescription>Nome, descrizione e tipo (attivo o subappalto) del listino.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-slate-700 text-sm">Nome</Label>
              <Input data-testid="listino-nome-input" value={listinoForm.nome} onChange={(e) => setListinoForm({ ...listinoForm, nome: e.target.value })} className="mt-1.5" placeholder="Es. Listino SIRTI 2026" autoFocus />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Descrizione</Label>
              <Textarea data-testid="listino-descrizione-input" value={listinoForm.descrizione} onChange={(e) => setListinoForm({ ...listinoForm, descrizione: e.target.value })} className="mt-1.5" rows={2} placeholder="Note o descrizione del listino (opzionale)" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Tipo</Label>
              <Select value={listinoForm.tipo} onValueChange={(v) => setListinoForm({ ...listinoForm, tipo: v })}>
                <SelectTrigger data-testid="listino-tipo-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="subappalto">Subappalto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setListinoDialog(false)}>Annulla</Button>
            <Button data-testid="save-listino-button" onClick={saveListino} className="bg-blue-700 hover:bg-blue-800">{editingListino ? "Salva" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-3xl w-[95vw] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FileUp className="h-5 w-5 text-blue-700" /> Importa in “{selected?.nome}”</DialogTitle>
            <DialogDescription>{importData ? `${importData.total} righe rilevate. Associa le colonne del file ai campi.` : ""}</DialogDescription>
          </DialogHeader>
          {importData && (
            <div className="space-y-5 py-1 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {MAP_FIELDS.map((f) => (
                  <div key={f.key} className="min-w-0">
                    <Label className="text-slate-700 text-sm">{f.label}{(f.key === "codice" || f.key === "descrizione") && <span className="text-blue-700"> *</span>}</Label>
                    <Select value={mapping[f.key] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid={`map-${f.key}`} className="mt-1.5 bg-white"><SelectValue placeholder="— Non mappato —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Non mappato —</SelectItem>
                        {importData.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Anteprima</div>
                <div className="border border-slate-200 rounded-lg overflow-auto max-h-60 min-w-0">
                  <table className="w-full text-sm font-mono-data">
                    <thead className="bg-slate-100 sticky top-0"><tr>{importData.columns.map((c) => <th key={c} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>
                      {importData.preview.map((row, i) => (
                        <tr key={`${importData.columns.map((c) => row[c]).join("|")}#${i}`} className="border-t border-slate-100">{importData.columns.map((c) => <td key={c} className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{row[c] == null ? "" : String(row[c])}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Annulla</Button>
            <Button data-testid="confirm-import-button" onClick={commitImport} disabled={importing} className="bg-blue-700 hover:bg-blue-800">
              {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Importa {importData ? `${importData.total} voci` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voce dialog */}
      <Dialog open={voceOpen} onOpenChange={setVoceOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingVoce ? "Modifica voce" : "Nuova voce"}</DialogTitle>
            <DialogDescription>Voce di capitolato con codice, descrizione, unità di misura e prezzo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700 text-sm">Codice</Label><Input data-testid="voce-codice-input" value={voceForm.codice} onChange={(e) => setVoceForm({ ...voceForm, codice: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-slate-700 text-sm">Unità di misura</Label><Input data-testid="voce-um-input" value={voceForm.unita_misura} onChange={(e) => setVoceForm({ ...voceForm, unita_misura: e.target.value })} className="mt-1.5" placeholder="m², kg, cad" /></div>
            </div>
            <div><Label className="text-slate-700 text-sm">Descrizione</Label><Input data-testid="voce-descrizione-input" value={voceForm.descrizione} onChange={(e) => setVoceForm({ ...voceForm, descrizione: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700 text-sm">Categoria</Label><Input data-testid="voce-categoria-input" value={voceForm.categoria} onChange={(e) => setVoceForm({ ...voceForm, categoria: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-slate-700 text-sm">Prezzo unitario €</Label><Input data-testid="voce-prezzo-input" type="number" step="0.01" value={voceForm.prezzo_unitario} onChange={(e) => setVoceForm({ ...voceForm, prezzo_unitario: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoceOpen(false)}>Annulla</Button>
            <Button data-testid="save-voce-button" onClick={saveVoce} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
